<script>
import { frontendURL } from '../../../helper/URLHelper';
import SecondaryNavItem from './SecondaryNavItem.vue';
import AccountContext from './AccountContext.vue';
import { mapGetters } from 'vuex';
import { FEATURE_FLAGS } from '../../../featureFlags';
import {
  getUserPermissions,
  hasPermissions,
} from '../../../helper/permissionsHelper';
import { routesWithPermissions } from '../../../routes';
import { useAlert } from 'dashboard/composables';
import { generateAllReportsPdf } from 'dashboard/helper/allReportsPdfHelper';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import { DATE_RANGE_OPTIONS } from 'dashboard/routes/dashboard/settings/reports/constants';
import { getUnixEndOfDay, getUnixStartOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';

export default {
  components: {
    AccountContext,
    SecondaryNavItem,
    WootDateRangePicker,
  },
  props: {
    accountId: {
      type: Number,
      default: 0,
    },
    labels: {
      type: Array,
      default: () => [],
    },
    inboxes: {
      type: Array,
      default: () => [],
    },
    teams: {
      type: Array,
      default: () => [],
    },
    customViews: {
      type: Array,
      default: () => [],
    },
    menuConfig: {
      type: Object,
      default: () => {},
    },
    currentUser: {
      type: Object,
      default: () => {},
    },
    isOnChatwootCloud: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['addLabel', 'toggleAccounts'],
  data() {
    return {
      isGeneratingReportsPdf: false,
      isPdfMenuOpen: false,
      selectedPdfDateRange: DATE_RANGE_OPTIONS.TODAY,
      pdfCustomDateRange: [new Date(), new Date()],
    };
  },
  computed: {
    ...mapGetters({
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    contactCustomViews() {
      return this.customViews.filter(view => view.filter_type === 'contact');
    },
    accessibleMenuItems() {
      const menuItemsFilteredByPermissions = this.menuConfig.menuItems.filter(
        menuItem => {
          const userPermissions = getUserPermissions(
            this.currentUser,
            this.accountId
          );
          return hasPermissions(
            routesWithPermissions[menuItem.toStateName],
            userPermissions
          );
        }
      );
      return menuItemsFilteredByPermissions.filter(item => {
        if (item.showOnlyOnCloud) {
          return this.isOnChatwootCloud;
        }
        return true;
      });
    },
    inboxSection() {
      return {
        icon: 'folder',
        label: 'INBOXES',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.INBOX_MANAGEMENT),
        newLinkTag: 'NEW_INBOX',
        key: 'inbox',
        toState: frontendURL(`accounts/${this.accountId}/settings/inboxes/new`),
        toStateName: 'settings_inbox_new',
        newLinkRouteName: 'settings_inbox_new',
        children: this.inboxes
          .map(inbox => ({
            id: inbox.id,
            label: inbox.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/inbox/${inbox.id}`
            ),
            type: inbox.channel_type,
            phoneNumber: inbox.phone_number,
            reauthorizationRequired: inbox.reauthorization_required,
          }))
          .sort((a, b) =>
            a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
          ),
      };
    },
    labelSection() {
      return {
        icon: 'number-symbol',
        label: 'LABELS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        key: 'label',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        dataTestid: 'sidebar-new-label-button',
        children: this.labels.map(label => ({
          id: label.id,
          label: label.title,
          color: label.color,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/label/${label.title}`
          ),
        })),
      };
    },
    contactLabelSection() {
      return {
        icon: 'number-symbol',
        label: 'TAGGED_WITH',
        hasSubMenu: true,
        key: 'label',
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        children: this.labels.map(label => ({
          id: label.id,
          label: label.title,
          color: label.color,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/labels/${label.title}/contacts`
          ),
        })),
      };
    },
    teamSection() {
      return {
        icon: 'people-team',
        label: 'TEAMS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_TEAM',
        key: 'team',
        toState: frontendURL(`accounts/${this.accountId}/settings/teams/new`),
        toStateName: 'settings_teams_new',
        newLinkRouteName: 'settings_teams_new',
        children: this.teams.map(team => ({
          id: team.id,
          label: team.name,
          truncateLabel: true,
          toState: frontendURL(`accounts/${this.accountId}/team/${team.id}`),
        })),
      };
    },
    foldersSection() {
      return {
        icon: 'folder',
        label: 'CUSTOM_VIEWS_FOLDER',
        hasSubMenu: true,
        key: 'custom_view',
        children: this.customViews
          .filter(view => view.filter_type === 'conversation')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/custom_view/${view.id}`
            ),
          })),
      };
    },
    contactSegmentsSection() {
      return {
        icon: 'folder',
        label: 'CUSTOM_VIEWS_SEGMENTS',
        hasSubMenu: true,
        key: 'custom_view',
        children: this.customViews
          .filter(view => view.filter_type === 'contact')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/contacts/custom_view/${view.id}`
            ),
          })),
      };
    },
    additionalSecondaryMenuItems() {
      let conversationMenuItems = [this.inboxSection, this.labelSection];
      let contactMenuItems = [this.contactLabelSection];
      if (this.teams.length) {
        conversationMenuItems = [this.teamSection, ...conversationMenuItems];
      }
      if (this.customViews.length) {
        conversationMenuItems = [this.foldersSection, ...conversationMenuItems];
      }
      if (this.contactCustomViews.length) {
        contactMenuItems = [this.contactSegmentsSection, ...contactMenuItems];
      }
      return {
        conversations: conversationMenuItems,
        contacts: contactMenuItems,
      };
    },
    isReportsNavigation() {
      return this.menuConfig.parentNav === 'reports';
    },
    pdfDateRangeOptions() {
      return Object.values(DATE_RANGE_OPTIONS).map(option => ({
        ...option,
        name: this.$t(option.translationKey),
      }));
    },
    selectedPdfPeriodLabel() {
      return this.$t(this.selectedPdfDateRange.translationKey);
    },
    isPdfCustomRange() {
      return (
        this.selectedPdfDateRange.id === DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id
      );
    },
    pdfPeriod() {
      const to = this.isPdfCustomRange
        ? getUnixEndOfDay(this.pdfCustomDateRange[1])
        : getUnixEndOfDay(new Date());
      const from = this.isPdfCustomRange
        ? getUnixStartOfDay(this.pdfCustomDateRange[0])
        : getUnixStartOfDay(
            subDays(new Date(), this.selectedPdfDateRange.offset)
          );

      return { from, to };
    },
  },
  methods: {
    showAddLabelPopup() {
      this.$emit('addLabel');
    },
    toggleAccountModal() {
      this.$emit('toggleAccounts');
    },
    showNewLink(featureFlag) {
      return this.isFeatureEnabledonAccount(this.accountId, featureFlag);
    },
    togglePdfMenu() {
      this.isPdfMenuOpen = !this.isPdfMenuOpen;
    },
    onPdfDateRangeChange(range) {
      this.selectedPdfDateRange = range;
      if (range.id !== DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id) {
        this.isPdfMenuOpen = false;
      }
    },
    onPdfCustomDateRangeChange(range) {
      this.pdfCustomDateRange = range;
    },
    async generateReportsPdf() {
      this.isGeneratingReportsPdf = true;
      try {
        await generateAllReportsPdf(this.pdfPeriod);
        this.isPdfMenuOpen = false;
      } catch {
        useAlert(this.$t('REPORT.PDF_GENERATION_FAILED'));
      } finally {
        this.isGeneratingReportsPdf = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col w-48 h-full px-2 pb-8 overflow-x-hidden overflow-y-auto text-sm bg-white border-r dark:bg-slate-900 dark:border-slate-800/50 rtl:border-r-0 rtl:border-l border-slate-50"
  >
    <AccountContext @toggle-accounts="toggleAccountModal" />
    <transition-group
      name="menu-list"
      tag="ul"
      class="pt-2 reset-base list-none"
    >
      <SecondaryNavItem
        v-for="menuItem in accessibleMenuItems"
        :key="menuItem.toState"
        :menu-item="menuItem"
      />
      <SecondaryNavItem
        v-for="menuItem in additionalSecondaryMenuItems[menuConfig.parentNav]"
        :key="menuItem.key"
        :menu-item="menuItem"
        @add-label="showAddLabelPopup"
      />
    </transition-group>
    <div
      v-if="isReportsNavigation"
      class="sticky bottom-0 px-1 pt-4 mt-auto bg-white border-t border-slate-100 dark:bg-slate-900 dark:border-slate-800"
    >
      <div
        v-if="isPdfMenuOpen"
        class="absolute left-1 right-1 bottom-full z-50 p-2 mb-2 border border-solid rounded-md shadow-lg bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-700"
      >
        <p
          class="px-2 mb-1 text-xs font-medium text-slate-500 dark:text-slate-400"
        >
          {{ selectedPdfPeriodLabel }}
        </p>
        <button
          v-for="option in pdfDateRangeOptions"
          :key="option.id"
          type="button"
          class="flex items-center w-full gap-2 px-2 py-1.5 text-sm text-left rounded-md text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
          :class="{
            'bg-woot-50 text-woot-700 dark:bg-woot-900/40 dark:text-woot-300':
              option.id === selectedPdfDateRange.id,
          }"
          @click="onPdfDateRangeChange(option)"
        >
          <fluent-icon
            v-if="option.id === selectedPdfDateRange.id"
            icon="checkmark"
            size="14"
          />
          <span v-else class="w-3.5" />
          <span>{{ option.name }}</span>
        </button>
        <WootDateRangePicker
          v-if="isPdfCustomRange"
          show-range
          append-to-body
          class="mt-2 pdf-date-picker"
          :value="pdfCustomDateRange"
          :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
          :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
          @change="onPdfCustomDateRangeChange"
        />
      </div>
      <div class="flex w-full max-w-full overflow-hidden split-pdf-button">
        <woot-button
          class-names="min-w-0 justify-center rounded-r-none"
          color-scheme="success"
          icon="arrow-download"
          :is-loading="isGeneratingReportsPdf"
          @click="generateReportsPdf"
        >
          {{ $t('REPORT.GENERATE_PDF') }}
        </woot-button>
        <woot-button
          class-names="flex-none rounded-l-none border-l border-white/20"
          color-scheme="success"
          :icon="isPdfMenuOpen ? 'chevron-down' : 'chevron-up'"
          :disabled="isGeneratingReportsPdf"
          :title="selectedPdfPeriodLabel"
          @click="togglePdfMenu"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.split-pdf-button :deep(.button) {
  margin: 0;
}

.split-pdf-button :deep(.button:first-child) {
  flex: 1 1 calc(100% - 2.5rem);
  width: calc(100% - 2.5rem);
  min-width: 0;
}

.split-pdf-button :deep(.button:first-child .button__content) {
  min-width: 0;
}

.split-pdf-button :deep(.button:first-child .spinner) {
  flex-shrink: 0;
  padding-left: 0;
  padding-right: 0;
}

.split-pdf-button :deep(.button--only-icon) {
  flex: 0 0 2.5rem;
  width: 2.5rem;
}

.pdf-date-picker :deep(.mx-datepicker) {
  width: 100%;
}
</style>
